// components/tracking/GoogleAdsTracker.tsx
'use client';

import {useEffect} from 'react';

type GoogleEventType = 'ViewContent' | 'Purchase' | 'ViewItemList' | 'PageView';

interface GoogleAdsTrackerProps {
  eventType: GoogleEventType;
  value?: string;
  currency?: string;
  eventId?: string; // Wird als transaction_id verwendet
  itemName?: string; // Wird aktuell nicht direkt im conversion snippet verwendet, aber gut für Kontext
  itemCategory?: string; // Wird aktuell nicht direkt im conversion snippet verwendet
}

/**
 * Sendet spezifische Google Ads Events. Für 'Purchase' wird das
 * 'conversion'-Event mit einem spezifischen Label aus Google Ads verwendet.
 */
export default function GoogleAdsTracker({
                                           eventType,
                                           value = '0',
                                           currency = 'EUR',
                                           eventId,
                                           itemName, // Kann für Debugging bleiben
                                           itemCategory, // Kann für Debugging bleiben
                                         }: GoogleAdsTrackerProps) {

  // Dein Google Ads Account ID (kann auch aus einer Konstante/Umgebungsvariable kommen)
  const googleAdsId = 'AW-16942094959';
  // !!! WICHTIG: Ersetze dies mit dem Label deiner KAUF-Conversion-Aktion aus Google Ads !!!
  const purchaseConversionLabel = 'WK2uCNu62LUaEO-0z44_';

  useEffect(() => {
    // Grundlegende Prüfungen
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      if (typeof window !== 'undefined') {
        console.warn('GoogleAdsTracker: gtag function not available when trying to send event.');
      }
      return;
    }

    const parsedValue = isNaN(Number(value)) ? 0 : Number(value);
    const normalizedEvent = typeof eventType === 'string' ? eventType.toLowerCase() : null;

    // --- Event-Logik ---

    // PageView ignorieren
    if (normalizedEvent === 'page_view') {
      console.log('[GoogleAdsTracker] Ignoring "PageView" event.');
      return;
    }

    // ViewContent behandeln (Standard-Event, ohne send_to)
    if (normalizedEvent === 'viewcontent' && eventId) {
      const viewItemData = {
        currency: currency,
        value: parsedValue,
        items: [{
          item_id: eventId,
          item_name: itemName,
          item_category: itemCategory,
          price: parsedValue,
          quantity: 1
        }]
      };
      console.log('[GoogleAdsTracker] Sending view_item event:', viewItemData);
      window.gtag('event', 'view_item', viewItemData);

      // *** HIER WIRD DER KAUF ALS SPEZIFISCHE CONVERSION GESENDET ***
    } else if (normalizedEvent === 'purchase' && eventId) {
      if (!purchaseConversionLabel) {
        console.error('[GoogleAdsTracker] Purchase Conversion Label constant is empty!');
        return;
      }
      const conversionData = {
        'send_to': `${googleAdsId}/${purchaseConversionLabel}`,
        'value': parsedValue,
        'currency': currency,
        'transaction_id': eventId // Eindeutige ID für diese Transaktion
      };

      console.log(`[GoogleAdsTracker] Sending specific 'conversion' event for Purchase:`, conversionData);
      window.gtag('event', 'conversion', conversionData);


      // Die folgende Zeile ist jetzt auskommentiert, da wir Methode 2 verwenden:
      // console.log(`[GoogleAdsTracker] Sending standard 'purchase' event:`, purchaseData);
      // window.gtag('event', 'purchase', purchaseData);


    } else if (normalizedEvent === 'viewitemlist') {
      console.log('[GoogleAdsTracker] Received "ViewItemList" - Implement tracking if needed.');
    }

  }, [eventType, value, currency, eventId, itemName, itemCategory, purchaseConversionLabel]); // Label als Dependency

  return null;
}